"use strict";
exports.__esModule = true;
var basic_table_component_1 = require("../basic-component/basic-table.component");
function applySpecialRules(table, specialRules) {
    if (!table || !table.length || !specialRules || !specialRules.length) {
        return table;
    }
    var colHeaders = table[0];
    specialRules.forEach(function (rule) {
        var ColHeader = rule.ColHeader, NewValue = rule.NewValue, OriginalValue = rule.OriginalValue, RULE = rule.RULE;
        var colIndex = colHeaders.indexOf(ColHeader);
        if (colIndex < 0) {
            return;
        }
        for (var i = 1; i < table.length; i++) {
            if (RULE === 'REPLACE' && table[i][colIndex].toString() === OriginalValue.toString()) {
                table[i][colIndex] = NewValue;
            }
        }
    });
    return table;
}
function displayRuleYear(displayRuleMap, key) {
    var displayRule = displayRuleMap[key];
    if (!displayRule) {
        return false;
    }
    // Due to template inconsistency.
    var rule = displayRule.RULE || displayRule.Rule || '';
    return rule.toUpperCase() === 'YEAR';
}
function displayRuleTooltip(displayRuleMap, key) {
    var displayRule = displayRuleMap[key];
    if (!displayRule) {
        return null;
    }
    var rule = displayRule.RULE || displayRule.Rule || '';
    return rule === 'TOOLTIP' ? displayRule.Tooltip : null;
}
function encodeFieldKey(key) {
    return btoa(key);
}
function formatCell(val, decimalPoint) {
    if (typeof val === 'undefined' || val === null) {
        return '""';
    }
    var str = val.toString().trim();
    if (!isNaN(val)) {
        return str.replace('.', decimalPoint);
    }
    return "\"" + str + "\"";
}
var SimpleTable = /** @class */ (function () {
    /**
     * An object helps to handle table data in a ui-grid friendly format.
     *
     * @param mainData { nodeName, nodePath, table: { tableData } }
     * @param siblingData { nodeName, nodePath, table: { tableData } }
     * @param tableConfig { displayRules, specialRules, isPnl, tableSettings: { decimals } }
     */
    function SimpleTable(mainData, siblingData, tableConfig) {
        this.tableConfig = tableConfig;
        this.rawMainData = this.applySpecialRules(mainData);
        this.rawSiblingData = this.applySpecialRules(siblingData);
        this.hasSibling = siblingData && siblingData.table;
        this.gridData = this.generateGridData();
    }
    SimpleTable.prototype.applySpecialRules = function (tableInfo) {
        if (!tableInfo || !tableInfo.table) {
            return {};
        }
        applySpecialRules(tableInfo.table.tableData, this.tableConfig.specialRules);
        return tableInfo;
    };
    SimpleTable.prototype.formatTable = function (tableInfo) {
        if (!tableInfo || !tableInfo.table) {
            return { colHeaders: [], rows: [] };
        }
        var nodeName = tableInfo.nodeName, nodePath = tableInfo.nodePath, tableData = tableInfo.table.tableData;
        var colHeaders = tableData[0].map(function (h) { return h.toString(); });
        var rows = tableData.slice(1).map(function (row) {
            var rowObj = { item: row[0], nodeName: nodeName, path: nodePath };
            colHeaders.forEach(function (h, colIndex) {
                rowObj[encodeFieldKey(h)] = row[colIndex];
            });
            return rowObj;
        });
        return { colHeaders: colHeaders, rows: rows };
    };
    SimpleTable.prototype.joinColHeaders = function (mainHeaders, siblingHeaders, headersAreYears) {
        if (!siblingHeaders || !siblingHeaders.length) {
            return mainHeaders;
        }
        var seenHeaders = {};
        var joinedHeaders = [];
        [mainHeaders, siblingHeaders].forEach(function (headers) {
            headers.forEach(function (h) {
                if (!seenHeaders[h]) {
                    seenHeaders[h] = true;
                    joinedHeaders.push(h);
                }
            });
        });
        return headersAreYears ? joinedHeaders.sort() : joinedHeaders;
    };
    SimpleTable.prototype.joinTableData = function (mainRows, siblingRows) {
        if (!siblingRows || !siblingRows.length) {
            return mainRows;
        }
        var siblingColor = '#f9a65a';
        var siblingRowMap = {};
        siblingRows.forEach(function (row) {
            row['color'] = siblingColor;
            siblingRowMap[row.item] = row;
        });
        var joinedRows = [];
        mainRows.forEach(function (row) {
            joinedRows.push(row);
            var siblingRow = siblingRowMap[row.item];
            if (siblingRow) {
                joinedRows.push(siblingRow);
            }
        });
        return joinedRows;
    };
    SimpleTable.prototype.generateGridData = function () {
        var mainTable = this.formatTable(this.rawMainData);
        var siblingTable = this.formatTable(this.rawSiblingData);
        var headersAreYears = this.tableConfig.isPnl;
        var joinedHeaders = this.joinColHeaders(mainTable.colHeaders.slice(1), siblingTable.colHeaders.slice(1), headersAreYears);
        var headerObjList = joinedHeaders.map(function (h) { return ({ field: encodeFieldKey(h), displayName: h }); });
        var leadingHeaders = [
            { field: 'path', displayName: 'Path', hide: true, isHeader: true },
            { field: 'nodeName', displayName: 'Project Nodes', hide: !this.hasSibling, isHeader: true },
            { field: 'item', displayName: 'Items', isHeader: true },
        ];
        return {
            headers: leadingHeaders.concat(headerObjList),
            rows: this.joinTableData(mainTable.rows, siblingTable.rows)
        };
    };
    SimpleTable.prototype.getColDef = function () {
        var _a = this.tableConfig, displayRules = _a.displayRules, isPnl = _a.isPnl, _b = _a.tableSettings, decimals = _b.decimals, userDefaultDecimals = _b.userDefaultDecimals;
        var headers = this.gridData.headers;
        var displayRuleMap = {};
        if (displayRules) {
            displayRules.forEach(function (dr) {
                displayRuleMap[dr.ColHeader] = dr;
            });
        }
        return headers.filter(function (h) { return !h.hide; }).map(function (h) {
            var field = h.field, displayName = h.displayName, isHeader = h.isHeader;
            var noFilter = isHeader || displayRuleYear(displayRuleMap, displayName) || userDefaultDecimals;
            var tooltip = displayRuleTooltip(displayRuleMap, displayName);
            return {
                enableHiding: false,
                enableSorting: !isPnl,
                field: field,
                displayName: displayName + (tooltip ? ' (?)' : ''),
                width: isHeader ? 150 : 100,
                pinnedLeft: isHeader,
                cellClass: isHeader ? 'grid-row-header' : null,
                headerTooltip: function () { return tooltip; },
                cellFilter: noFilter ? '' : "tableNumber: " + decimals
            };
        });
    };
    SimpleTable.prototype.getGridOptions = function () {
        var rows = this.gridData.rows;
        var colDef = this.getColDef();
        var timestamp = this.tableConfig.tableSettings.timestampMode === 'lastRecalc' ? this.tableConfig.timestamp : null;
        return {
            columnDefs: colDef,
            rowTemplate: '<div style="text-overflow: ellipsis" ng-style="{\'color\': row.entity.color }">' +
                '    <div style="text-overflow: ellipsis" ng-if="!row.entity.merge" ' +
                '       ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" ' +
                '       class="ui-grid-cell" ng-class="{ \'ui-grid-row-header-cell\': col.isRowHeader }" ui-grid-cell>' +
                '  </div>' +
                '</div>',
            data: rows,
            showGridFooter: true,
            gridFooterTemplate: '<div class="ui-grid-bottom-panel" style="text-align: right; color: #939294; font-size: 10px">' +
                basic_table_component_1.getDate(timestamp) + '</div>'
        };
    };
    SimpleTable.prototype.getCsv = function (csvFormat) {
        if (csvFormat === void 0) { csvFormat = 'NA'; }
        var _a = this.gridData, headers = _a.headers, rows = _a.rows;
        if (!headers) {
            return '';
        }
        var cellSeparator = csvFormat === 'EU' ? ';' : ',';
        var decimalPoint = csvFormat === 'EU' ? ',' : '.';
        var headerLine = headers.map(function (header) { return formatCell(header.displayName, decimalPoint); }).join(cellSeparator);
        var contentLines = rows.map(function (row) {
            return headers.map(function (col) {
                var cell = row[col.field];
                return formatCell(cell, decimalPoint);
            }).join(cellSeparator);
        });
        contentLines.unshift(headerLine);
        return contentLines.join('\n');
    };
    return SimpleTable;
}());
exports["default"] = SimpleTable;
//# sourceMappingURL=simple-table.logic.js.map


// WEBPACK FOOTER //
// ./app/widgets/action/simple-table/simple-table.logic.js